/**
 * Responsibility: show a card's photographs in the space of one.
 *
 * The catalogue render says what the card is; the rest are photographs of
 * copies that actually sold, which is what shows centring and edges. Both
 * matter, and stacking them pushed the chart and the links below the fold -
 * so they share one frame and the arrows move between them.
 *
 * The arrows appear on hover rather than sitting there permanently. With one
 * picture there is nothing to move between and they never appear at all,
 * which is the common case and should not carry chrome for the rare one.
 *
 * Deliberately smaller than the single image it replaces. The dialog has to
 * hold figures, a chart and a row of links without scrolling, and the
 * picture was the thing taking the room.
 */
import { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import { CardThumb } from './CardThumb';

type ImageCarouselProps = {
  images: string[];
  alt: string;
  width?: number;
  onZoom?: (src: string) => void;
};

/** Small enough that the chart and links still fit under it. */
const DEFAULT_WIDTH = 150;

export function ImageCarousel({
  images,
  alt,
  width = DEFAULT_WIDTH,
  onZoom,
}: ImageCarouselProps) {
  const [index, setIndex] = useState(0);

  // A different card can have fewer photographs than the one before it.
  useEffect(() => setIndex(0), [images]);

  if (images.length === 0) {
    return <CardThumb src={null} alt={alt} width={width} />;
  }

  const safe = Math.min(index, images.length - 1);
  const current = images[safe];
  const many = images.length > 1;
  const step = (by: number) => setIndex((images.length + safe + by) % images.length);

  return (
    <Stack spacing={0.5} alignItems="center" sx={{ flexShrink: 0 }}>
      <Box
        sx={{
          position: 'relative',
          lineHeight: 0,
          // The arrows are hidden until the frame is hovered, so a single
          // photograph shows nothing but the photograph.
          '&:hover .carousel-arrow': { opacity: 1 },
        }}
      >
        <Box
          onClick={() => onZoom?.(current)}
          sx={{ cursor: onZoom ? 'zoom-in' : 'default', lineHeight: 0 }}
        >
          <CardThumb src={current} alt={alt} width={width} />
        </Box>

        {many ? (
          <>
            <IconButton
              className="carousel-arrow"
              size="small"
              aria-label="Previous photo"
              onClick={() => step(-1)}
              sx={arrowStyle('left')}
            >
              <ChevronLeftIcon fontSize="small" />
            </IconButton>
            <IconButton
              className="carousel-arrow"
              size="small"
              aria-label="Next photo"
              onClick={() => step(1)}
              sx={arrowStyle('right')}
            >
              <ChevronRightIcon fontSize="small" />
            </IconButton>
          </>
        ) : null}
      </Box>

      <Stack direction="row" spacing={0.5} alignItems="center">
        {onZoom ? <ZoomInIcon sx={{ fontSize: 13, color: 'text.disabled' }} /> : null}
        <Typography variant="caption" color="text.secondary">
          {many ? `${safe + 1} of ${images.length} · click to enlarge` : 'click to enlarge'}
        </Typography>
      </Stack>
    </Stack>
  );
}

function arrowStyle(side: 'left' | 'right') {
  return {
    position: 'absolute' as const,
    top: '50%',
    [side]: 2,
    transform: 'translateY(-50%)',
    opacity: 0,
    transition: 'opacity 120ms',
    bgcolor: 'background.paper',
    border: '1px solid',
    borderColor: 'divider',
    '&:hover': { bgcolor: 'background.paper' },
    // Always reachable on touch, where there is no hover to reveal them.
    '@media (pointer: coarse)': { opacity: 1 },
  };
}
